import React, { useRef, useState } from 'react';

import classNames from 'classnames';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import SanitizedHTML from 'components/ui/sanitized_html';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { Progress } from '@buildoutinc/blueprint-react/ui/Progress';

import { DescribeStep } from './describe_step';
import {
  PlanReviewStep, toReviewedPlan, toReviewPages, type ReviewPage,
} from './plan_review_step';
import { useDocumentComposer, type DocumentReview, type ReviewedPlan } from './use_document_composer';

const TEMPLATE_PICKER_ID = 'new-doc-modal';

type Step = 'describe' | 'review';
// The request a failure came from, which decides what Try again repeats and which step shows it.
type Attempt = 'plan' | 'review' | 'build';

const STEPS: { label: string; step: Step }[] = [
  { label: 'Describe', step: 'describe' },
  { label: 'Review', step: 'review' },
];
// Em-based so the circle scales with the stepper's text.
const STEP_MARKER_SIZE = { height: '2em', width: '2em' };

// The reviewed plan being edited and the prompt it was planned from, so Continue with that prompt returns to it.
interface PlanDraft {
  pages: ReviewPage[];
  prompt: string;
  review: DocumentReview;
}

interface Props {
  folderId: number;
  propertyId: number;
  templateCount: number;
}

export default function AiNewDocument({ folderId, propertyId, templateCount }: Props) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [lastPrompt, setLastPrompt] = useState('');
  const [step, setStep] = useState<Step>('describe');
  const [draft, setDraft] = useState<PlanDraft | null>(null);
  const [attempt, setAttempt] = useState<Attempt>('plan');
  const browseOnClose = useRef(false);
  const {
    build, compose, error, fetchReview, phase, reply, reset, retryable, status,
  } = useDocumentComposer({ folderId, propertyId });
  const busy = phase === 'planning' || phase === 'reviewing' || phase === 'building';
  // A build failure belongs to Review, where its plan is; Describe shows the others.
  const buildFailed = phase === 'failed' && attempt === 'build';

  function stepState(index: number) {
    const current = STEPS.findIndex(({ step: value }) => value === step);
    if (index < current) return 'complete';

    return index === current ? 'active' : 'upcoming';
  }

  function handleTriggerClick(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    setOpen(true);
  }

  // Closing drops the run, so a late frame or build reply can't act on a dismissed dialog.
  function close() {
    reset();
    setOpen(false);
    setStep('describe');
    setDraft(null);
    setAttempt('plan');
  }

  // A build under way still creates the document, so the dialog stays to take the user to it.
  function handleOpenChange(isOpen: boolean) {
    if (isOpen) {
      setOpen(true);
    } else if (phase !== 'building') {
      close();
    }
  }

  function browseTemplates() {
    browseOnClose.current = true;
    close();
  }

  // The template picker is a Bootstrap modal; opening it before this one finishes closing races the two
  // over the body's scroll lock and focus.
  function handleOpenChangeComplete(isOpen: boolean) {
    if (!isOpen && browseOnClose.current) {
      browseOnClose.current = false;
      const picker = document.getElementById(TEMPLATE_PICKER_ID);
      if (picker) {
        window.bootstrap.Modal.getOrCreateInstance(picker).show();
      }
    }
  }

  async function handleContinue(nextPrompt: string) {
    setLastPrompt(nextPrompt);
    if (draft?.prompt === nextPrompt) {
      setStep('review');
      return;
    }

    setDraft(null);
    setAttempt('plan');
    if (await compose(nextPrompt)) await loadReview(nextPrompt);
  }

  async function loadReview(forPrompt: string) {
    setAttempt('review');
    const review = await fetchReview();
    if (review) {
      setDraft({ pages: toReviewPages(review.plan.pages), prompt: forPrompt, review });
      setStep('review');
    }
  }

  function handleCreate(plan: ReviewedPlan) {
    setAttempt('build');
    void build(plan);
  }

  // A review that failed without a verdict is fetched again for the same chat; anything else re-plans.
  function retryDescribe() {
    if (attempt === 'review' && retryable) {
      void loadReview(lastPrompt);
    } else {
      void handleContinue(lastPrompt);
    }
  }

  function handlePagesChange(pages: ReviewPage[]) {
    setDraft(current => current && { ...current, pages });
  }

  function renderProgress(label: string) {
    return (
      <div className="d-flex flex-column gap-2">
        <Progress aria-label={label} value={null} variant="indeterminate" />
        <div className="small">{label}</div>
      </div>
    );
  }

  function renderFailure(onTryAgain: (() => void) | null) {
    return (
      <Alert className="d-flex align-items-center gap-3" role="alert" severity="destructive">
        <span className="flex-grow-1">{error}</span>
        {onTryAgain && <Button autoFocus label="Try again" size="sm" variant="secondary" onClick={onTryAgain} />}
      </Alert>
    );
  }

  // A plan the builder rejected (422) stays here to be fixed, so only other failures offer Try again, which
  // sends the pages as they are now.
  function renderReviewFailure() {
    if (!buildFailed || !draft) return null;

    const retry = () => build(toReviewedPlan(draft.pages, draft.review.plan.name));
    return renderFailure(retryable ? retry : null);
  }

  // Describe's busy label; Review shows its own while building.
  function busyLabel() {
    return phase === 'planning' ? `${status ?? 'Reading your templates'}…` : 'Preparing your plan…';
  }

  function renderStatus() {
    if (busy) return renderProgress(busyLabel());

    if (phase === 'replied') {
      return (
        <Alert severity="info">
          <SanitizedHTML html={reply ?? ''} />
        </Alert>
      );
    }

    return null;
  }

  return (
    <>
      <a className="dropdown-item" href="#" onClick={handleTriggerClick}>
        <span>
          <i className="far fa-file" />
        </span>
        New Document
      </a>

      <Modal
        open={open}
        onOpenChange={handleOpenChange}
        onOpenChangeComplete={handleOpenChangeComplete}
      >
        <Modal.Content centered size="lg">
          {/* The stepper spans the body's width, so the header drops the end padding kept for the close button. */}
          <Modal.Header className="gap-3 pe-3">
            <Modal.Title>New Document</Modal.Title>
            <ol aria-label="Steps" className="list-unstyled d-flex align-items-center gap-3 w-100 mb-0 small">
              {STEPS.map(({ label, step: value }, index) => {
                const state = stepState(index);

                return (
                  <li
                    aria-current={state === 'active' ? 'step' : undefined}
                    className={classNames('d-flex align-items-center gap-3', { 'flex-grow-1': index > 0 })}
                    key={value}
                  >
                    {index > 0 && (
                      <span
                        aria-hidden="true"
                        className={classNames('flex-grow-1 border-top', { 'border-primary': state !== 'upcoming' })}
                      />
                    )}
                    <span
                      className={classNames(
                        'd-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-circle border',
                        'fw-semibold',
                        state === 'upcoming' ? 'text-muted' : 'text-bg-primary border-primary'
                      )}
                      style={STEP_MARKER_SIZE}
                    >
                      {state === 'complete' ? <FontAwesomeIcon aria-label="Completed" icon={faCheck} /> : index + 1}
                    </span>
                    <span
                      className={classNames(state === 'upcoming' ? 'text-muted' : 'text-primary', {
                        'fw-semibold': state === 'active',
                      })}
                    >
                      {label}
                    </span>
                  </li>
                );
              })}
            </ol>
          </Modal.Header>

          {step === 'review' && draft ? (
            <PlanReviewStep
              alert={renderReviewFailure()}
              disabled={phase === 'building'}
              notice={phase === 'building' ? renderProgress('Creating your document…') : null}
              pages={draft.pages}
              review={draft.review}
              onBack={() => setStep('describe')}
              onCreate={handleCreate}
              onPagesChange={handlePagesChange}
            />
          ) : (
            <DescribeStep
              alert={phase === 'failed' && !buildFailed ? renderFailure(retryDescribe) : null}
              disabled={busy}
              notice={renderStatus()}
              prompt={prompt}
              templateCount={templateCount}
              onBrowseTemplates={browseTemplates}
              onContinue={handleContinue}
              onPromptChange={setPrompt}
            />
          )}
        </Modal.Content>
      </Modal>
    </>
  );
}
